import React from "react";
import { observer } from "mobx-react-lite";
import { Modal } from "@lib/modal";

//#region lazy

import { getCurrentCulture } from "@utils";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { isCapacitorPlatform } from "@utils/specific/capacitor";
import FavoritePopup from "./FavoritePopup";
import OnboardingRegisterPopup from "./OnboardingRegisterPopup";
import OnboardingNewsletterPopup from "./OnboardingNewsletterPopup";
import MostPlayTicketsPopup from "@features/quick-play/mobile/most-played-tickets/MostPlayTicketsPopup";
import {
	ChatBetSlipSharedPopup,
	SupportChatInfoPopup,
} from "@features/chat/mobile";
import {
	DenyUserMatchingConfirmationPopup,
	UseMarketplaceBonusConfirmPopup,
} from ".";
import BetBuilderPopup from "./BetBuilderPopup";
import SharedBetSlipPopup from "@features/quick-play/mobile/shared-bet-slips/components/SharedBetSlipPopup";
import { PopupName } from "@components/popups/shared/PopupName";
import BlockPopupService from "@services/user/BlockPopupService";
import {
	BetBuilderPicksNotAvailablePopup,
	BetBuilderPicksPopup,
} from "@features/quick-play/mobile/bet-builder-picks";
import { useRootAppStore } from "@hooks";
import { MobilePlatform } from "@state/stores/types";
import CountryChangePopup from "./CountryChangePopup";
import BankIdFlowSelectPopup from "./BankIdFlowSelectPopup";
import BankIdQrCodePopup from "./BankIdQrCodePopup";
import RestrictionTypeDenyPopup from "./RestrictionTypeDenyPopup";
import GracePeriodConfirmationPopup from "../shared/GracePeriodConfirmationPopup";
import BettingTipsPopup from "@features/quick-play/mobile/betting-tips/BettingTipsPopup";
import { UserDataConfirmAuthPopup } from "@features/membership/user-data-confirm/shared";
import { LogoutPopup } from "../shared";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const RealityCheckPopup = lazy(loadFailPath, ()=>import("./RealityCheckPopup"))
// prettier-ignore
const AutomaticLogoutPopup = lazy(loadFailPath, ()=>import("./AutomaticLogoutPopup"))
// prettier-ignore
const User24hLockPopup = lazy(loadFailPath, ()=>import("./User24hLockPopup"))
// prettier-ignore
const AccountVerificationPopup = lazy(loadFailPath, ()=>import("./AccountVerificationPopup"));
// prettier-ignore
const WelcomeLoginPopup = lazy(loadFailPath,() => import("./WelcomeLoginPopup"));
//prettier-ignore
const FailedLoginsPopup = lazy(loadFailPath, () => import("./FailedLoginsPopup"));
//prettier-ignore
const LiveSessionExpirePopup = lazy(
	loadFailPath,
	() => import("./LiveSessionExpirePopup")
);
//prettier-ignore
const RequiredUserActionsPopup = lazy(loadFailPath, () => import("./RequiredUserActionsPopup"))
//prettier-ignore
const AppPopup = lazy(loadFailPath, () => import("./AppPopup"));

//#endregion lazy

export default observer(function PopupDisplay() {
	return (
		<Modal>
			<Popup />
		</Modal>
	);
});

const Popup = observer(() => {
	const rootStore = useRootAppStore<MobilePlatform>();
	const {
		isRestrictionTypeDeny,
		automaticLogoutStore: { isActive: isAutomaticLogoutActive },
		realityCheckStore: { isActive: isRealityCheckActive },
		user24hLockStore: { isActive: isUser24hLockActive },
		accountVerificationStore: { isActive: isAccountVerificationActive },
		welcomeStore: {
			isActive: isWelcomePopupActive,
			isUseBonusConfirmActive,
			hideUseBonusConfirmationPopup,
		},
		failedLoginsCheckPopupStore: { isActive: isFailedLoginsPopupActive },
		onboardingWelcomePopupStore: {
			onboardingNewsletterStore: {
				isActive: isOnboardingNewsletterPopupActive,
			},
			onboardingRegistrationStore: {
				isActive: isOnboardingRegisterPopupActive,
			},
		},
		requiredUserActionStore: { isActive: RequiredUserActionsPopupActive },
		offerHub: { isSessionExpired: isOfferHubSessionExpire },
		cashoutHubStore: { isSessionExpired: isCashOutHubSessionExpire },
		capacitorUpdaterStore: {
			isLoading: isCapacitorUpdateDownloading,
			isPopupVisible: isCapacitorUpdateActive,
		},
		favoritePopupStore: { isActive: isFavoritePopupActive },
		quickPlayStore: {
			mostPlayedTicketsStore: {
				isPopupDisplayed: isQuickPlayPopupActive,
			},
			sharedBetSlipStore: {
				isPopupDisplayed: isSharedBetSlipPopupActive,
			},
			betBuilderPicksStore: {
				isPopupDisplayed: isBetBoosterPresetPopupActive,
				BBPickNotAvailablePopup: isBBPickNoAvailablePopupActive,
			},
			bettingTipsStore: { isPopupDisplayed: isBettingTipsActive },
		},
		cookieConsentStore: {
			shouldBlockOtherPopups: isCookieConsentPopupActive,
		},
		appPopupStore: { isAppPopupVisible },
		chatViewStore: {
			chatBetSlipShareData: isChatBetSlipSharePopupActive,
			supportChatInfoPopup,
		},
		userAuthStore,
		sportStatisticsHub: {
			isSessionExpired: isSportStatisticsHubSessionExpired,
		},
		betBuilderPopupStore: { isActive: isBetBuilderPopupActive },
		countryChangeStore: {
			isCountryChangePopupActive: isCountryChangePopupActive,
		},
		bankIdCapacitorStore: {
			isBankIdFlowPopupActive: isBankIdFlowPopupActive,
			isBankIdQrCodePopupActive: isBankIdQrCodePopupActive,
		},
		gracePeriodStore: { isActive: isGracePeriodConfirmationActive },
		logoutPopupStore: { isLogoutPopupActive },
	} = rootStore;

	if (
		(isAutomaticLogoutActive ||
			isRealityCheckActive ||
			isUser24hLockActive ||
			isAccountVerificationActive ||
			isWelcomePopupActive ||
			isFailedLoginsPopupActive ||
			isOnboardingNewsletterPopupActive ||
			isOnboardingRegisterPopupActive ||
			RequiredUserActionsPopupActive ||
			isOfferHubSessionExpire ||
			isCashOutHubSessionExpire ||
			isCapacitorUpdateDownloading ||
			isCapacitorUpdateActive ||
			isFavoritePopupActive ||
			isQuickPlayPopupActive ||
			isBBPickNoAvailablePopupActive ||
			isBetBoosterPresetPopupActive ||
			isSharedBetSlipPopupActive ||
			isBettingTipsActive ||
			isCookieConsentPopupActive) &&
		rootStore.chatViewStore.chatBoxVisible
	) {
		rootStore.chatViewStore.toggleChatBox(false);
	}

	if (
		isCapacitorPlatform() &&
		(isCapacitorUpdateActive || isCapacitorUpdateDownloading)
	) {
		//we dont want to show any popups if popup for updating needs to be shown or downloading loader
		return null;
	}

	if (isRestrictionTypeDeny && isCapacitorPlatform())
		return <RestrictionTypeDenyPopup />;

	if (isCookieConsentPopupActive) {
		//do not render any popups if consent is shown
		return null;
	}

	if (isLogoutPopupActive) {
		return <LogoutPopup />;
	}

	if (
		isRealityCheckActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.RealityCheck)
	) {
		return <RealityCheckPopup isOpened={isRealityCheckActive} />;
	}

	if (
		isAutomaticLogoutActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.AutomaticLogout)
	) {
		return <AutomaticLogoutPopup isOpened={isAutomaticLogoutActive} />;
	}
	if (
		(isOfferHubSessionExpire ||
			isCashOutHubSessionExpire ||
			isSportStatisticsHubSessionExpired) &&
		!window.location.pathname.includes("games") &&
		!BlockPopupService.checkIfPopupBlocked(
			PopupName.SessionExpire || PopupName.CashoutExpire
		)
	) {
		return <LiveSessionExpirePopup />;
	}

	if (isBBPickNoAvailablePopupActive) {
		return <BetBuilderPicksNotAvailablePopup />;
	}

	if (
		//check
		isBetBuilderPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.BetBuilder)
	) {
		return <BetBuilderPopup />;
	}
	if (
		isQuickPlayPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.QuickPlay)
	) {
		return <MostPlayTicketsPopup />;
	}

	if (isBetBoosterPresetPopupActive) {
		return <BetBuilderPicksPopup />;
	}

	if (
		isSharedBetSlipPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.SharedBetSlip)
	) {
		return <SharedBetSlipPopup />;
	}

	if (
		isBettingTipsActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.BettingTips)
	) {
		return (
			<BettingTipsPopup
				bettingTipsStore={rootStore.quickPlayStore.bettingTipsStore}
			/>
		);
	}

	if (isAppPopupVisible) {
		return <AppPopup />;
	}

	if (
		isAccountVerificationActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.AccountVerification)
	) {
		return (
			<AccountVerificationPopup isOpened={isAccountVerificationActive} />
		);
	}

	if (
		isUser24hLockActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.User24LockActive)
	) {
		return <User24hLockPopup />;
	}

	if (
		isWelcomePopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.WelcomePopup)
	) {
		return <WelcomeLoginPopup />;
	}

	if (
		isUseBonusConfirmActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.UseBonus)
	) {
		return (
			<UseMarketplaceBonusConfirmPopup
				hidePopup={hideUseBonusConfirmationPopup}
				sendConfirmation={(value: boolean) =>
					userAuthStore.sendWelcomeUseBonusConfirmation(value)
				}
			/>
		);
	}

	if (
		isFailedLoginsPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.FailedLogins)
	) {
		return <FailedLoginsPopup />;
	}

	if (
		isFavoritePopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.FavoritePopup)
	) {
		return <FavoritePopup />;
	}

	if (
		isOnboardingRegisterPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.OnboardingRegister)
	) {
		return (
			<OnboardingRegisterPopup
				isOpened={isOnboardingRegisterPopupActive}
			/>
		);
	}

	if (
		isOnboardingNewsletterPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.OnboardingNewsletter)
	) {
		return (
			<OnboardingNewsletterPopup
				isOpened={isOnboardingNewsletterPopupActive}
			/>
		);
	}

	if (
		RequiredUserActionsPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.RequiredUserActions)
	) {
		if (userAuthStore.user) {
			return <RequiredUserActionsPopup />;
		}
	}

	if (
		isChatBetSlipSharePopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.ChatBetSlipShare)
	) {
		return <ChatBetSlipSharedPopup />;
	}

	if (isCountryChangePopupActive) {
		return <CountryChangePopup />;
	}

	if (supportChatInfoPopup) {
		return <SupportChatInfoPopup />;
	}

	if (
		isCapacitorPlatform() &&
		isBankIdFlowPopupActive &&
		WEBPACK_IS_BANKID_ENABLED
	) {
		return <BankIdFlowSelectPopup />;
	}

	if (
		isCapacitorPlatform() &&
		isBankIdQrCodePopupActive &&
		WEBPACK_IS_BANKID_ENABLED
	) {
		return <BankIdQrCodePopup />;
	}

	if (isGracePeriodConfirmationActive) {
		return <GracePeriodConfirmationPopup />;
	}

	if (
		userAuthStore.userDataConfirmationStore
			.isUserDataConfirmationPopupActive
	) {
		return <UserDataConfirmAuthPopup />;
	}

	if (userAuthStore.userMatchingStore.isDenyConfirmationPopupActive) {
		return <DenyUserMatchingConfirmationPopup />;
	}

	return null;
});
